/* eslint-disable require-yield */
import {
  PayloadAction,
  createAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit'
import { Task as SagaTask, eventChannel } from 'redux-saga'
import {
  all,
  call,
  cancel,
  cancelled,
  delay,
  fork,
  put,
  race,
  select,
  spawn,
  take,
  takeEvery,
} from 'redux-saga/effects'

import * as api from '../api'
import * as utils from '../utils'
// Needed by makeSelectCategoriesWithMedia. helpers does not import redux, so
// this direction introduces no cycle - checked before adding it.
import { camAngleKinds, categoryToMediaKinds } from '../helpers/helpers'
import {
  FireEvent,
  FireSnapshot,
  Storage,
  getDatabase,
  getStorage,
} from '../api'
import {
  extractMediaKind,
  MediaCaptureWithBackup,
  MediaCapture,
  MediaItem,
  MediaKind,
  processMediaItem,
  SectionMediaCapture,
} from '../schema'
import {
  EMPTY_ARRAY,
  EMPTY_OBJ,
  DeepReadonly,
  createEventBatcher,
  keys,
  log,
  processErr,
  values,
} from '../utils'

import { IS_WEB } from '../env'

import { YieldReturn } from './common'
import { selectPlainThumbnail } from './thumbnails'

const PREFIX = 'media'

export interface MediaItemToBeUploaded {
  customerID: string
  description?: string
  height?: number
  name: string
  ofWhat: MediaKind
  path: string
  thumbnail: string
  type: 'image' | 'video'
  width?: number
}

export type Media = Record<string, MediaItem>

export type MediaKindToMedia = Record<MediaKind, Media>

type CustomerToKinds = Record<string, MediaKindToMedia>

interface WritableMediaState {
  customerToKinds: CustomerToKinds
}

type MediaState = DeepReadonly<WritableMediaState>

const initCustomerMediaIfNeeded = (
  customerToKinds: CustomerToKinds,
  customerID: string,
) => {
  customerToKinds[customerID] =
    customerToKinds[customerID] || emptyMediaKindToMedia()

  if (
    customerToKinds[customerID] &&
    (!customerToKinds[customerID]?.solar_proposal ||
      !customerToKinds[customerID]?.all_exterior_walls_site_survey ||
      !customerToKinds[customerID]?.rafters_site_survey ||
      !customerToKinds[customerID]?.electrical_panel_site_survey ||
      !customerToKinds[customerID]?.all_planes_roof_site_survey ||
      !customerToKinds[customerID]?.other_site_survey ||
      !customerToKinds[customerID]?.attic_site_survey ||
      !customerToKinds[customerID]?.attic_site_survey ||
      !customerToKinds[customerID]?.house360_site_survey ||
      !customerToKinds[customerID]?.roof_site_survey ||
      !customerToKinds[customerID]?.hvac_system ||
      !customerToKinds[customerID]?.windows ||
      !customerToKinds[customerID]?.rc_front_house ||
      !customerToKinds[customerID]?.rc_address_verification ||
      !customerToKinds[customerID]?.rc_3d_view ||
      !customerToKinds[customerID]?.rc_solar_panels ||
      !customerToKinds[customerID]?.rc_wires_home ||
      !customerToKinds[customerID]?.rc_siding ||
      !customerToKinds[customerID]?.rc_other_property_wind_damage ||
      !customerToKinds[customerID]?.rc_wind_damages ||
      !customerToKinds[customerID]?.rc_layers ||
      !customerToKinds[customerID]?.rc_roof_inclination ||
      !customerToKinds[customerID]?.rc_roof_overview ||
      !customerToKinds[customerID]?.rc_gutters ||
      !customerToKinds[customerID]?.rc_flashings ||
      !customerToKinds[customerID]?.rc_penetrations ||
      !customerToKinds[customerID]?.rc_hail ||
      !customerToKinds[customerID]?.rc_length_shingle ||
      !customerToKinds[customerID]?.rc_drip_edge ||
      !customerToKinds[customerID]?.rc_shingles_color ||
      !customerToKinds[customerID]?.rc_garage ||
      !customerToKinds[customerID]?.rc_ice_shield ||
      !customerToKinds[customerID]?.testimonial_video ||
      !customerToKinds[customerID]?.signing_recording)
  ) {
    customerToKinds[customerID] = {
      attic_site_survey: customerToKinds[customerID]?.attic_site_survey || {},
      hvac_system: customerToKinds[customerID]?.hvac_system || {},
      house360_site_survey:
        customerToKinds[customerID]?.house360_site_survey || {},
      roof_site_survey: customerToKinds[customerID]?.roof_site_survey || {},
      adders: customerToKinds[customerID]?.adders || {},
      electrical: customerToKinds[customerID]?.electrical || {},
      electricity: customerToKinds[customerID]?.electricity || {},
      id: customerToKinds[customerID]?.id || {},
      insulation: customerToKinds[customerID]?.insulation || {},
      owner_house: customerToKinds[customerID]?.owner_house || {},
      problem: customerToKinds[customerID]?.problem || {},
      roof: customerToKinds[customerID]?.roof || {},
      temperature: customerToKinds[customerID]?.temperature || {},
      thermal: customerToKinds[customerID]?.thermal || {},
      other_documents: customerToKinds[customerID]?.other_documents || {},
      solar_proposal: customerToKinds[customerID]?.solar_proposal || {},
      all_exterior_walls_site_survey:
        customerToKinds[customerID]?.all_exterior_walls_site_survey || {},
      rafters_site_survey:
        customerToKinds[customerID]?.rafters_site_survey || {},
      electrical_panel_site_survey:
        customerToKinds[customerID]?.electrical_panel_site_survey || {},
      all_planes_roof_site_survey:
        customerToKinds[customerID]?.all_planes_roof_site_survey || {},
      other_site_survey: customerToKinds[customerID]?.other_site_survey || {},
      all_appliances_and_labels_site_survey:
        customerToKinds[customerID]?.all_appliances_and_labels_site_survey ||
        {},
      eight_foot_msp_site_survey:
        customerToKinds[customerID]?.eight_foot_msp_site_survey || {},
      existing_solar_system_site_survey:
        customerToKinds[customerID]?.existing_solar_system_site_survey || {},
      ground_system_connected_site_survey:
        customerToKinds[customerID]?.ground_system_connected_site_survey || {},
      main_breaker_disconnect_closeup_site_survey:
        customerToKinds[customerID]
          ?.main_breaker_disconnect_closeup_site_survey || {},
      main_panel_breakers_dead_front_included_site_survey:
        customerToKinds[customerID]
          ?.main_panel_breakers_dead_front_included_site_survey || {},
      main_panel_breakers_dead_front_removed_site_survey:
        customerToKinds[customerID]
          ?.main_panel_breakers_dead_front_removed_site_survey || {},
      main_panel_label_site_survey:
        customerToKinds[customerID]?.main_panel_label_site_survey || {},
      main_service_panel_site_survey:
        customerToKinds[customerID]?.main_service_panel_site_survey || {},
      mini_split_surroundings:
        customerToKinds[customerID]?.mini_split_surroundings || {},
      sub_panels_site_survey:
        customerToKinds[customerID]?.sub_panels_site_survey || {},
      utility_meter_closeup_site_survey:
        customerToKinds[customerID]?.utility_meter_closeup_site_survey || {},
      windows: customerToKinds[customerID]?.windows || {},
      rc_front_house: customerToKinds[customerID]?.rc_front_house || {},
      rc_address_verification:
        customerToKinds[customerID]?.rc_address_verification || {},
      rc_3d_view: customerToKinds[customerID]?.rc_3d_view || {},
      rc_solar_panels: customerToKinds[customerID]?.rc_solar_panels || {},
      rc_wires_home: customerToKinds[customerID]?.rc_wires_home || {},
      rc_siding: customerToKinds[customerID]?.rc_siding || {},
      rc_other_property_wind_damage:
        customerToKinds[customerID]?.rc_other_property_wind_damage || {},
      rc_wind_damages: customerToKinds[customerID]?.rc_wind_damages || {},
      rc_layers: customerToKinds[customerID]?.rc_layers || {},
      rc_roof_inclination:
        customerToKinds[customerID]?.rc_roof_inclination || {},
      rc_roof_overview: customerToKinds[customerID]?.rc_roof_overview || {},
      rc_gutters: customerToKinds[customerID]?.rc_gutters || {},
      rc_flashings: customerToKinds[customerID]?.rc_flashings || {},
      rc_penetrations: customerToKinds[customerID]?.rc_penetrations || {},
      rc_hail: customerToKinds[customerID]?.rc_hail || {},
      rc_length_shingle: customerToKinds[customerID]?.rc_length_shingle || {},
      rc_drip_edge: customerToKinds[customerID]?.rc_drip_edge || {},
      rc_shingles_color: customerToKinds[customerID]?.rc_shingles_color || {},
      rc_garage: customerToKinds[customerID]?.rc_garage || {},
      rc_ice_shield: customerToKinds[customerID]?.rc_ice_shield || {},
      security_cam0: customerToKinds[customerID]?.security_cam0 || {},
      security_cam1: customerToKinds[customerID]?.security_cam1 || {},
      security_cam2: customerToKinds[customerID]?.security_cam2 || {},
      security_cam3: customerToKinds[customerID]?.security_cam3 || {},
      security_cam4: customerToKinds[customerID]?.security_cam4 || {},
      security_cam5: customerToKinds[customerID]?.security_cam5 || {},
      security_cam6: customerToKinds[customerID]?.security_cam6 || {},
      security_cam7: customerToKinds[customerID]?.security_cam7 || {},
      security_camera_locations:
        customerToKinds[customerID]?.security_camera_locations || {},
      security_camera_locations_s4:
        customerToKinds[customerID]?.security_camera_locations_s4 || {},
      security_camera_locations_s3:
        customerToKinds[customerID]?.security_camera_locations_s3 || {},
      security_camera_locations_s2:
        customerToKinds[customerID]?.security_camera_locations_s2 || {},
      security_camera_locations_indoor:
        customerToKinds[customerID]?.security_camera_locations_indoor || {},
      security_doorbell_locations:
        customerToKinds[customerID]?.security_doorbell_locations || {},
      security_360_house_view:
        customerToKinds[customerID]?.security_360_house_view || {},
      security_attic: customerToKinds[customerID]?.security_attic || {},
      security_router_location:
        customerToKinds[customerID]?.security_router_location || {},
      security_post_camera_installation:
        customerToKinds[customerID]?.security_post_camera_installation || {},
      security_post_alarm_installation:
        customerToKinds[customerID]?.security_post_alarm_installation || {},
      security_cctv: customerToKinds[customerID]?.security_cctv || {},
      cancellation_letter:
        customerToKinds[customerID]?.cancellation_letter || {},
      testimonial_video: customerToKinds[customerID]?.testimonial_video || {},
      signing_recording: customerToKinds[customerID]?.signing_recording || {},
    }
  }
}

const mediaSlice = createSlice({
  name: PREFIX,
  initialState: {
    customerToKinds: {},
  } as MediaState,
  reducers: {
    beganUpload(
      { customerToKinds }: WritableMediaState,
      {
        payload: { items },
      }: PayloadAction<{
        items: MediaItem[]
      }>,
    ) {
      for (const mediaItem of items) {
        const { order_id: customerID, name: mediaItemID } = mediaItem
        const mediaKind = extractMediaKind(mediaItem)

        initCustomerMediaIfNeeded(customerToKinds, customerID)

        const item = customerToKinds[customerID]?.[mediaKind]?.[mediaItemID]

        if (!item) {
          throw new ReferenceError(
            `Could not find item for coordinates: ${customerID}-${mediaKind}-${mediaItemID}`,
          )
        }
        item.status = 'uploading'
        // A (re)upload attempt starting means any previous failure no longer
        // applies - without this, a retry that succeeds would still carry
        // the old error message, and a retry still in flight would keep
        // showing the retry icon from ticket 12800490828 instead of the
        // spinner, since both read off this same field.
        item.err = ''
      }
    },
    draftForMediaUpload(
      { customerToKinds },
      {
        payload: { item },
      }: PayloadAction<{
        item: MediaItemToBeUploaded
      }>,
    ) {
      const { customerID, name, ofWhat, path, type } = item

      if (!name) {
        throw new TypeError(`File name not provided for item.`)
      }

      initCustomerMediaIfNeeded(customerToKinds, customerID)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      customerToKinds[customerID]![ofWhat][name] = {
        description: item.description || '',
        height:
          item.height || customerToKinds[customerID]![ofWhat][name]!.height,
        err: '',
        image_of:
          type === 'image'
            ? ofWhat === 'owner_house'
              ? ofWhat
              : ofWhat + '_pictures'
            : '',
        name,
        order_id: customerID,
        path,
        status: 'draft',
        thumbnail: item.thumbnail,
        updated_at: Date.now(),
        url: '',
        video_of: type === 'video' ? ofWhat + '_videos' : '',
        width: item.width || customerToKinds[customerID]![ofWhat][name]!.width,
      }
    },
    removedDraftMediaItem(
      { customerToKinds },
      {
        payload,
      }: PayloadAction<{ customerID: string; ofWhat: MediaKind; name: string }>,
    ) {
      delete customerToKinds[payload.customerID]![payload.ofWhat][payload.name]
    },
    removeAllDraftByCustomer(
      { customerToKinds },
      { payload }: PayloadAction<{ customerID: string }>,
    ) {
      // TODO: Check non-null assertions
      for (const ofWhat of utils.keys(customerToKinds[payload.customerID]!)) {
        const _ofWhat = ofWhat as MediaKind
        const mediaListToDelete = utils
          .keys(customerToKinds[payload.customerID]![_ofWhat])
          .filter(
            (name) =>
              customerToKinds[payload.customerID]![_ofWhat]![name]!.status ===
              'draft',
          )
        for (const name of mediaListToDelete) {
          delete customerToKinds[payload.customerID]![_ofWhat][name]
        }
      }
    },
    receivedMediaFBEvents(
      { customerToKinds },
      {
        payload: { events },
      }: PayloadAction<{ events: FireEvent<MediaItem>[] }>,
    ) {
      // Cast: At least one event should be in there
      const {
        item: { order_id: customerID },
      } = events[0]!

      initCustomerMediaIfNeeded(customerToKinds, customerID)

      for (const { key, item: mediaItem, type } of events) {
        if (type === 'child_added' || type === 'child_changed') {
          const mediaKind = extractMediaKind(mediaItem)

          const customerMediaOfThisKind =
            customerToKinds[customerID]![mediaKind]

          // The server is authoritative for everything EXCEPT `path`, which it
          // deliberately does not store: the record is written with
          // `path: ''` because a file location on one device means nothing to
          // any other client.
          //
          // Replacing the whole item therefore erased the app's own pointer to
          // a photo it had just taken. The sequence, all within seconds and
          // with no restart involved: the rep takes a photo and Redux holds its
          // real path; the saga writes the record to the database with a blank
          // path; the subscription echoes that write straight back; this line
          // overwrote the local item with it. From then on the photo has no
          // path, so a stalled upload can never be retried - `handleRetryPress`
          // reports it as "no longer on this device" and `retryStalledUploads`
          // skips it, because both test `path`.
          //
          // The file was on the device the whole time. Found by Samuel on
          // build 724 doing exactly what a rep does: photo with no signal,
          // wait for it to fail, reconnect, tap retry.
          //
          // An incoming path is still honoured if one is ever present, so this
          // only defends the case the server has nothing to say about.
          const local = customerMediaOfThisKind[key]

          customerMediaOfThisKind[key] = {
            ...mediaItem,
            path: mediaItem.path || local?.path || '',
          }
        }
        if (type === 'child_removed') {
          const kindToMedia = customerToKinds[customerID]!

          for (const media of values(kindToMedia)) {
            delete media[key]
          }
        }
      }
    },
    updatedUploadProgress(
      { customerToKinds },
      {
        payload: { customerID, mediaItemID, mediaKind, percent },
      }: PayloadAction<{
        customerID: string
        mediaItemID: string
        mediaKind: MediaKind
        percent: number
      }>,
    ) {
      const item = customerToKinds[customerID]?.[mediaKind]?.[mediaItemID]
      if (item) {
        item.progress = percent
      }
    },
    requestedMediaDeletion(
      { customerToKinds },
      {
        payload: { customerID, mediaItemID, mediaKind },
      }: PayloadAction<{
        customerID: string
        mediaItemID: string
        mediaKind: MediaKind
      }>,
    ) {
      initCustomerMediaIfNeeded(customerToKinds, customerID)

      delete customerToKinds[customerID]?.[mediaKind]?.[mediaItemID]
    },
    requestedMediaUpload(
      { customerToKinds },
      {
        payload: { items },
      }: PayloadAction<{
        items: MediaItemToBeUploaded[]
      }>,
    ) {
      for (const item of items) {
        const { customerID, name, ofWhat, path, type } = item

        if (!name) {
          throw new TypeError(`File name not provided for item.`)
        }

        initCustomerMediaIfNeeded(customerToKinds, customerID)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const currItem = customerToKinds[customerID]![ofWhat][name]

        if (currItem) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          currItem.status = 'pending'
        } else {
          customerToKinds[customerID]![ofWhat][name] = {
            description: item.description || '',
            err: '',
            image_of:
              type === 'image'
                ? ofWhat === 'owner_house'
                  ? ofWhat
                  : ofWhat + '_pictures'
                : '',
            name,
            order_id: customerID,
            path: IS_WEB ? '' : path,
            status: 'pending',
            thumbnail: '',
            updated_at: Date.now(),
            url: '',
            video_of: type === 'video' ? ofWhat + '_videos' : '',
          }
        }
      }
    },
    mediaUploadTimedOut(
      { customerToKinds },
      {
        payload: { customerID, mediaItemID, mediaKind },
      }: PayloadAction<{
        customerID: string
        mediaItemID: string
        mediaKind: MediaKind
      }>,
    ) {
      const item = customerToKinds[customerID]?.[mediaKind]?.[mediaItemID]

      if (item) {
        item.status = 'pending'
        // 12849137033: this used to set only the status, and that made the
        // retry affordance built in 12800490828 unreachable in the single most
        // common failure - losing signal mid-upload. MediaGroupItem shows the
        // retry icon on `err && status !== 'uploaded'`, so with err left empty
        // a timed-out item fell through to the same spinner as one still
        // uploading. The photo was parked doing nothing, looked identical to
        // one in flight, and only resumed if the rep happened to leave
        // Documentation and come back.
        //
        // Worded as stalled rather than failed on purpose: the upload has not
        // errored, it has gone quiet. If the connection was merely slow this
        // still reads truthfully, and the rep gets a button either way.
        item.err = 'Upload stalled - tap to retry'
      }
    },
  },
  extraReducers(builder) {
    return builder.addCase('persist/REHYDRATE', ({ customerToKinds }) => {
      for (const mediaByKind of values(customerToKinds)) {
        for (const customerMedia of values(mediaByKind)) {
          for (const mediaItem of values(customerMedia)) {
            if (mediaItem.status === 'uploading') {
              mediaItem.status = 'pending'
            }
          }
        }
      }
    })
  },
})

export const {
  actions: {
    beganUpload,
    draftForMediaUpload,
    mediaUploadTimedOut,
    receivedMediaFBEvents,
    requestedMediaUpload,
    removedDraftMediaItem,
    removeAllDraftByCustomer,
    requestedMediaDeletion,
    updatedUploadProgress,
  },
  reducer: media,
} = mediaSlice

export const subToCustomerMedia = createAction<{ customerID: string }>(
  `${PREFIX}/subToCustomerMedia`,
)

export const unSubFromCustomerMedia = createAction<{ customerID: string }>(
  `${PREFIX}/unSubFromCustomerMedia`,
)

//#region selectors

interface GlobalState {
  [PREFIX]: MediaState
}

export const selectAllMediaCustomers = ({
  media: { customerToKinds },
}: GlobalState): CustomerToKinds => {
  if (Object.keys(customerToKinds).length === 0) {
    // Reduce re-renders
    return EMPTY_OBJ
  }
  return customerToKinds
}

export interface SelectCustomerHasMediaParams {
  customerID: string
}

export type SelectCustomerHasMedia = (
  _: GlobalState,
  args: SelectCustomerHasMediaParams,
) => boolean

export const makeSelectCustomerHasMedia = (): SelectCustomerHasMedia =>
  createSelector(
    selectAllMediaCustomers,
    (_: GlobalState, args: SelectCustomerHasMediaParams) => args,
    (customerToKinds, { customerID }): boolean => {
      const mediaKinds = customerToKinds[customerID]
      if (!mediaKinds) {
        return false
      }
      for (const mediaOfThatKind of values(mediaKinds)) {
        if (keys(mediaOfThatKind).length > 0) {
          return true
        }
      }
      return false
    },
  )

export interface SelectCategoriesWithMediaParams {
  customerID: string
}

export type SelectCategoriesWithMedia = (
  _: GlobalState,
  args: SelectCategoriesWithMediaParams,
) => Readonly<Record<string, boolean>>

/**
 * Which Documentation-tab sections already hold media for this customer
 * (Monday 12803784158). Feeds `shouldShowCategory`, which keeps a section
 * reachable when it has photos even after its product is unticked.
 *
 * Returns the whole map in one memoized shot rather than exposing a
 * per-section selector: the caller needs every section at once, and handing it
 * a fresh object on every render would re-render the tab constantly. Each
 * `makeSelect*` call gets its own memoization, which is why these are
 * factories - one instance per component.
 *
 * Drafts count on purpose: a photo mid-upload still belongs to the rep, and
 * its section has to stay reachable to watch it finish or retry it.
 */
/**
 * Every media kind that visually belongs to a category, which is NOT the same
 * as its entry in categoryToMediaKinds.
 *
 * The 8 Cam angle slots and the CCTV title are rendered inside the Smart Home
 * accordion but are deliberately absent from the map - Documentation.tsx builds
 * them itself, and helpers.ts says so in place: "not listed in
 * categoryToMediaKinds below".
 *
 * Reading the map alone therefore reported Smart Home as empty for a customer
 * whose only security photos were Cam angles, which let the section be hidden
 * with those photos still inside it - unreachable to view, re-note or delete.
 * That is exactly the failure the hasMedia escape hatch exists to prevent, and
 * it failed on the one group that does not live in the map.
 */
const kindsToCheck = (category: string): readonly MediaKind[] =>
  category === 'Smart Home'
    ? [
        ...(categoryToMediaKinds[category] || EMPTY_ARRAY),
        ...camAngleKinds,
        'security_cctv',
      ]
    : categoryToMediaKinds[category] || EMPTY_ARRAY

export const makeSelectCategoriesWithMedia = (): SelectCategoriesWithMedia =>
  createSelector(
    selectAllMediaCustomers,
    (_: GlobalState, args: SelectCategoriesWithMediaParams) => args.customerID,
    (customerToKinds, customerID): Readonly<Record<string, boolean>> => {
      const forCustomer = customerToKinds[customerID]
      const out: Record<string, boolean> = {}
      for (const category of keys(categoryToMediaKinds)) {
        out[category] = forCustomer
          ? kindsToCheck(category).some((mediaKind: MediaKind) => {
              const mediaOfThatKind = forCustomer[mediaKind]
              return !!mediaOfThatKind && keys(mediaOfThatKind).length > 0
            })
          : false
      }
      return out
    },
  )

export interface SelectMediaKeysParams {
  customerID: string
  mediaKind: MediaKind
}

export const makeSelectMediaKeys = () =>
  createSelector(
    selectAllMediaCustomers,
    (_: GlobalState, args: SelectMediaKeysParams) => args,
    (customerToKinds, { customerID, mediaKind }): string[] => {
      if (Object.keys(customerToKinds).length === 0) {
        // Reduce re-renders
        return EMPTY_ARRAY as string[]
      }
      const items = (customerToKinds[customerID]?.[mediaKind] ||
        EMPTY_OBJ) as Media

      if (Object.keys(items).length === 0) {
        // Reduce re-renders
        return EMPTY_ARRAY as string[]
      }
      return utils
        .values(items)
        .sort((a, b) => a.updated_at - b.updated_at)
        .filter((item) => item.status !== 'draft')
        .map((item) => item.name)
    },
  )

export const makeSelectDraftMediaKeys =
  (customerID: string) =>
  ({
    media: { customerToKinds },
  }: GlobalState): {
    sectionMediaCapture: SectionMediaCapture[]
    allMedia: MediaCapture[]
  } => {
    if (Object.keys(customerToKinds).length === 0) {
      // Reduce re-renders
      return {
        sectionMediaCapture: EMPTY_ARRAY as SectionMediaCapture[],
        allMedia: EMPTY_ARRAY as MediaCapture[],
      }
    }
    const mediaKinds = (customerToKinds[customerID] ||
      EMPTY_ARRAY) as MediaKindToMedia

    const items: SectionMediaCapture[] = []
    let allMedia: MediaCapture[] = []

    for (const mediaOfThatKind of keys(mediaKinds)) {
      const data: MediaCaptureWithBackup[] = []

      for (const mediaItem of values(mediaKinds[mediaOfThatKind])) {
        if (mediaItem.status !== 'draft') {
          continue
        }
        const mediaCapture = {
          height: mediaItem.height,
          path: mediaItem.thumbnail,
          width: mediaItem.width,
        } as any
        data.push({
          id: mediaItem.name,
          description: mediaItem.description || '',
          mediaCapture: {
            media: mediaCapture,
            type: 'photo',
          },
        })
        const metadata = {
          type: 'photo',
          media: {
            height: mediaItem.height || 0,
            path: mediaItem.thumbnail,
            width: mediaItem.width || 0,
          },
        } as any
        allMedia.push(metadata)
      }

      if (!data.length) {
        continue
      }
      items.push({
        title: mediaOfThatKind,
        data,
      })
    }
    return { sectionMediaCapture: items, allMedia }
  }

export interface SelectMediaParams {
  customerID: string
  mediaKind: MediaKind
}

export const makeSelectMedia = () =>
  createSelector(
    selectAllMediaCustomers,
    (_: GlobalState, args: SelectMediaParams) => args,
    (customerToKinds, { customerID, mediaKind }): Media => {
      if (Object.keys(customerToKinds).length === 0) {
        // Reduce re-renders
        return EMPTY_OBJ
      }
      const items = customerToKinds[customerID]?.[mediaKind] || EMPTY_OBJ

      if (Object.keys(items).length === 0) {
        // Reduce re-renders
        return EMPTY_OBJ
      }
      return items
    },
  )

export interface SelectMediaItemParams {
  customerID: string
  mediaItemID: string
  mediaKind: MediaKind
}

export const makeSelectMediaItem = () => {
  const selectCustomerMedia = makeSelectMedia()

  return createSelector(
    selectCustomerMedia,
    (_: GlobalState, args: SelectMediaItemParams) => args,
    (mediaOfThatKind, { customerID, mediaItemID, mediaKind }): MediaItem => {
      const res = mediaOfThatKind[mediaItemID]

      if (res) {
        return res
      }

      return {
        description: '',
        err: '',
        image_of: mediaKind + 'pictures',
        name: mediaItemID,
        order_id: customerID,
        path: '',
        status: 'pending',
        thumbnail: '',
        updated_at: 0,
        url: '',
        video_of: '',
      }
    },
  )
}

export interface SelectAllCustomerMediaParams {
  readonly customerID: string
}

export const makeSelectAllCustomerMedia = () =>
  createSelector(
    selectAllMediaCustomers,
    (_: GlobalState, args: SelectAllCustomerMediaParams) => args,
    (allMediaCustomers, { customerID }): MediaKindToMedia => {
      return allMediaCustomers[customerID] || emptyMediaKindToMedia()
    },
  )

//#endregion selectors

//#region mediaChannel

type MediaChannelPayload = FireEvent<MediaItem>[]

const mediaSubscription =
  (
    mediaType: 'picture' | 'video',
    eventType: 'child_added' | 'child_changed' | 'child_removed',
    listener: (e: FireEvent<MediaItem> | Error) => void,
  ) =>
  (data: FireSnapshot, prevKey?: string | null) => {
    try {
      const { key } = data

      if (!key) {
        throw new ReferenceError(`${mediaType} ${eventType} without key`)
      }

      const val = data.val()

      const item = processMediaItem(val)

      listener({
        type: eventType,
        item,
        key,
        prevKey,
      })
    } catch (e) {
      log(`Error inside ${mediaType} subscription (${eventType}):`)
      log(e)
      if (e instanceof Error) {
        listener(e)
      }
    }
  }

const createMediaChannel = (customerID: string) =>
  eventChannel<MediaChannelPayload | Error>((emit) => {
    const database = getDatabase()
    const picsRef = database.ref(`NewPics/${customerID}`)
    const vidsRef = database.ref(`Videos/${customerID}`)

    const eventBatcher = createEventBatcher<FireEvent<MediaItem>>({
      debounceTime: 1000,
    })

    eventBatcher.onEvents(emit)

    picsRef.on(
      'child_added',
      mediaSubscription('picture', 'child_added', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    picsRef.on(
      'child_changed',
      mediaSubscription('picture', 'child_changed', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    picsRef.on(
      'child_removed',
      mediaSubscription('picture', 'child_removed', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    vidsRef.on(
      'child_added',
      mediaSubscription('video', 'child_added', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    vidsRef.on(
      'child_changed',
      mediaSubscription('video', 'child_changed', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    vidsRef.on(
      'child_removed',
      mediaSubscription('video', 'child_removed', (e) => {
        if (e instanceof Error) {
          eventBatcher.flush()
          emit(e)
        } else {
          eventBatcher.eventReceived(e)
        }
      }),
    )

    return () => {
      eventBatcher.flush()
      eventBatcher.off()
      picsRef.off()
      vidsRef.off()
    }
  })

function* subToMedia(customerID: string) {
  const mediaChannel: YieldReturn<typeof createMediaChannel> = yield call(
    createMediaChannel,
    customerID,
  )

  try {
    while (true) {
      const payload: MediaChannelPayload = yield take(mediaChannel)

      yield put(
        receivedMediaFBEvents({
          events: payload,
        }),
      )

      // Lydia Lee, Oct 16: records left at 'pending' whose file DID reach
      // Storage (the upload finished after the app had stopped listening, see
      // watchLateUploadFinish). Their device path is gone once the app
      // restarts, so no retry could ever send them, and the web shows only
      // 'uploaded' items. Checked as they arrive, on any device that opens
      // the customer (the web included), once per item per session.
      yield fork(healStuckItems, payload)
    }
  } catch (e) {
    log(e)
  } finally {
    if ((yield cancelled()) as boolean) {
      mediaChannel.close()
    }
  }
}

// B40: items reverted to 'pending' by mediaUploadTimedOut (or by the
// persist/REHYDRATE 'uploading'->'pending' conversion on app restart) are
// never automatically retried otherwise. Re-attempting whenever the rep
// (re)opens Documentation for this customer is the natural, low-effort
// resume point — mirrors "retry on next app foreground/mount".
function* retryStalledUploads(customerID: string) {
  const customerMedia: MediaKindToMedia | undefined = (
    (yield select()) as GlobalState
  ).media.customerToKinds[customerID]

  if (!customerMedia) {
    return
  }

  const itemsToRetry: MediaItemToBeUploaded[] = []

  for (const mediaKind of keys(customerMedia)) {
    for (const item of values(customerMedia[mediaKind])) {
      if (item.status === 'pending' && item.path) {
        itemsToRetry.push({
          customerID,
          description: item.description,
          name: item.name,
          ofWhat: mediaKind,
          path: item.path,
          thumbnail: item.thumbnail,
          type: item.video_of ? 'video' : 'image',
        })
      }
    }
  }

  if (itemsToRetry.length > 0) {
    yield put(requestedMediaUpload({ items: itemsToRetry }))
  }
}

function* mediaSubWatcher() {
  try {
    while (true) {
      const action: ReturnType<typeof subToCustomerMedia> = yield take(
        subToCustomerMedia,
      )

      const {
        payload: { customerID },
      } = action

      const mediaSubTask: SagaTask = yield fork(subToMedia, customerID)

      yield call(retryStalledUploads, customerID)

      yield take(unSubFromCustomerMedia)

      yield cancel(mediaSubTask)
    }
  } catch (e) {
    log(`Error inside subscription (${e}):`)
  }
}

//#endregion mediaChannel

/** True when the item's file is in Storage with some bytes in it. */
const isInStorage = async (item: MediaItem): Promise<boolean> => {
  const ref = getStorage().ref(item.order_id + '/' + item.name)
  if (!ref.getMetadata) return false
  try {
    const meta = await ref.getMetadata()
    return Number(meta.size) > 0
  } catch {
    // No file at this key (or no connection): leave the record as it is
    return false
  }
}

/** Records a finished upload; the round trip updates the screen. */
const markUploaded = (item: MediaItem): void => {
  const isPic = !!item.image_of
  void getDatabase()
    .ref(
      isPic
        ? `NewPics/${item.order_id}/${item.name}`
        : `Videos/${item.order_id}/${item.name}`,
    )
    .update({ err: '', path: '', status: 'uploaded' })
}

const healChecked = new Set<string>()

function* healStuckItems(events: MediaChannelPayload) {
  for (const event of events) {
    const { item } = event
    if (event.type === 'child_removed' || item.status !== 'pending') continue
    if (healChecked.has(item.name) || itemsBeingProcessed.has(item.name)) {
      continue
    }
    healChecked.add(item.name)
    const stored = (yield call(isInStorage, item)) as boolean
    if (stored) {
      log(`[media] ${item.name} was already in Storage, marked uploaded`)
      markUploaded(item)
    }
  }
}

/**
 * After UPLOAD_TIMEOUT_MS of silence the upload saga gives up and parks the
 * item at 'pending', but the native transfer keeps going and often finishes
 * later (Lydia Lee's 98 MB signing video finished ~7 h after). Nobody was
 * listening any more, so the record stayed 'pending' with the file already
 * in Storage. This keeps listening to the same transfer, without a timeout,
 * and records the success when it arrives; an error just ends it (the
 * retry paths are unchanged).
 */
function* watchLateUploadFinish(
  uploadChannel: ReturnType<typeof createUploadChannel>,
  item: MediaItem,
) {
  try {
    while (true) {
      const payload = (yield take(uploadChannel)) as UploadChannelPayload
      if (payload instanceof Error) return
      if (payload.type === 'success') {
        log(`[media] late upload finished for ${item.name}`)
        markUploaded(item)
        return
      }
    }
  } catch {
    // The transfer failed after all (an Error on a channel is thrown by
    // take): nothing to record, the retry paths handle it
  } finally {
    uploadChannel.close()
  }
}

type UploadChannelPayload =
  | { type: 'success' }
  | { type: 'progress'; percent: number }
  | Error

const createUploadChannel = (
  blobOrPath: Blob | string,
  ref: ReturnType<Storage['ref']>,
) =>
  eventChannel<UploadChannelPayload | Error>((emit) => {
    const task = ref.putFile(blobOrPath)

    // This channel only ever saw progress. react-native-firebase fans the task
    // out over three separate native events and routes each to a different
    // argument of on() — see subscribeToEvents() in StorageTask.js:
    //
    //   'state_changed' -> nextOrObserver   (arg 2)
    //   'failure'       -> error            (arg 3)
    //   'success'       -> complete         (arg 4)
    //
    // Passing only arg 2 subscribed to progress and nothing else, so a finished
    // upload and a failed one were both silently dropped: the snapshot.state
    // === 'success' and snapshot.error branches below could never be reached on
    // native, because neither event is delivered over 'state_changed' at all.
    // That is the literal complaint in the ticket — fires neither success nor
    // error — and it is why every upload sat spinning until UPLOAD_TIMEOUT_MS
    // eventually forced it back to 'pending'.
    const unsubscribe = task.on(
      'state_changed',
      (snapshot) => {
        if (
          snapshot.totalBytes != null &&
          snapshot.totalBytes > 0 &&
          snapshot.bytesTransferred != null
        ) {
          emit({
            type: 'progress',
            percent: Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
            ),
          })
        }
      },
      (err) => {
        emit(err instanceof Error ? err : new Error(String(err)))
      },
      () => {
        emit({ type: 'success' })
      },
    )

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    }
  })

const base64ToBlob = async (base64: string): Promise<Blob> => {
  log(`[media] base64ToBlob: input length=${base64.length}`)
  const blob = await (await fetch(base64)).blob()
  log(`[media] base64ToBlob: blob size=${blob.size} type=${blob.type}`)
  return blob
}

// B40: putFile()'s state_changed channel can stall forever (app backgrounded
// mid-transfer, network dropped without a native error callback) with no
// terminal event ever emitted — neither the success nor error branch below
// would run, so nothing would ever reach Firebase and the item would spin
// forever. This timeout guarantees the generator always reaches a terminal
// state, reverting to 'pending' so retryStalledUploads can retry it.
//
// 12849137033: lowered from 60s. This is an INACTIVITY timeout, not a total
// duration - the race below re-arms on every progress event, and Firebase
// emits one per chunk transferred, so a slow-but-moving upload never trips it.
// Sixty seconds of complete silence therefore only ever meant "the rep waits a
// full minute before being allowed to do anything about it".
//
// Fifteen rather than ten: on a weak cellular link a ten-second gap between
// progress events is plausible with nothing actually broken, and a false trip
// now surfaces to the rep. Fifteen keeps that margin while still handing them
// control quickly. Safe to be this short precisely because the message says
// stalled rather than failed - which is true either way - and because the
// recovery is a retry, not a discarded photo.
const UPLOAD_TIMEOUT_MS = 15000

function* uploadMediaItem(item: MediaItem) {
  log(
    `[media] uploadMediaItem start: name=${item.name} video_of=${item.video_of} image_of=${item.image_of} pathLen=${item.path.length}`,
  )
  if (!item.path) {
    throw new ReferenceError(`MediaItem ${item.name} without path`)
  }

  try {
    const blobOrPath: YieldReturn<typeof base64ToBlob> | string = IS_WEB
      ? yield call(base64ToBlob, item.path)
      : item.path

    log(
      `[media] uploadMediaItem: blob ready, starting Firebase upload for ${item.name}`,
    )
    const fileDBRef = getStorage().ref(item.order_id + '/' + item.name)
    const uploadChannel = createUploadChannel(blobOrPath, fileDBRef)

    // Loop instead of a single race(): a 'progress' event is not terminal,
    // the upload keeps running and will emit more events (more progress,
    // then eventually error/success) - only those end the saga. Each
    // iteration re-arms the timeout, so an upload only times out after
    // UPLOAD_TIMEOUT_MS with no events at all, not after a fixed total
    // duration regardless of progress.
    while (true) {
      const { payload, timedOut } = (yield race({
        payload: take(uploadChannel),
        timedOut: delay(UPLOAD_TIMEOUT_MS),
      })) as {
        payload?: UploadChannelPayload
        timedOut?: boolean
      }

      if (timedOut) {
        log(`Upload timed out for ${item.name}, reverting to pending for retry`)
        // The transfer may still finish: keep listening to it on its own
        yield spawn(watchLateUploadFinish, uploadChannel, item)
        yield put(
          mediaUploadTimedOut({
            customerID: item.order_id,
            mediaItemID: item.name,
            mediaKind: extractMediaKind(item),
          }),
        )
        return
      }

      if (payload instanceof Error) {
        const database = getDatabase()
        const isPic = !!item.image_of
        // Ticket 12800490828: MediaItemStatus has no 'error' member (only
        // draft/pending/uploading/uploaded), so a genuinely failed upload
        // cannot be told apart from one still in flight by status alone -
        // MediaGroupItem.tsx shows a spinner for anything that is not
        // 'uploaded'. `err` being non-empty is what now lets the UI offer a
        // retry instead of spinning forever, without needing to widen that
        // type and touch every exhaustive switch over it elsewhere.
        const newItem = {
          ...item,
          err: processErr(payload.message),
        }

        const itemRef = database.ref(
          isPic
            ? `NewPics/${item.order_id}/${item.name}`
            : `Videos/${item.order_id}/${item.name}`,
        )
        log(`Error inside uploadMediaItem: ${processErr(payload.message)}`)
        itemRef.update(newItem)
        return
      }

      if (payload?.type === 'progress') {
        yield put(
          updatedUploadProgress({
            customerID: item.order_id,
            mediaItemID: item.name,
            mediaKind: extractMediaKind(item),
            percent: payload.percent,
          }),
        )
        continue
      }

      if (payload?.type === 'success') {
        const isPic = !!item.image_of

        const database = getDatabase()

        const itemRef = database.ref(
          isPic
            ? `NewPics/${item.order_id}/${item.name}`
            : `Videos/${item.order_id}/${item.name}`,
        )

        const newItem: MediaItem = {
          ...item,
          err: '',
          path: '',
          status: 'uploaded',
        }

        // let the round trip handle the state update
        itemRef.update(newItem as unknown as Record<string, unknown>)

        if (item.image_of === 'owner_house') {
          api._updateCustomer(item.order_id, {
            owner_house: item.thumbnail,
          })
        }
        return
      }

      log(`Error inside uploadMediaItem: Unexpected payload type`)
      throw new Error(`Unexpected payload type`)
    }
  } finally {
    itemsBeingProcessed.delete(item.name)
  }
}

const itemsBeingProcessed = new Set<string>()

function* mediaUploadWatcher({
  payload: { items },
}: ReturnType<typeof requestedMediaUpload>) {
  const currentBatchToBeProcessed = [] as MediaItem[]
  try {
    for (const item of items) {
      if (itemsBeingProcessed.has(item.name)) {
        continue
      }
      itemsBeingProcessed.add(item.name)

      const thumbnail = selectPlainThumbnail(item.name)(yield select())
      currentBatchToBeProcessed.push({
        description: item.description || '',
        err: '',
        image_of:
          item.type === 'image'
            ? item.ofWhat === 'owner_house'
              ? item.ofWhat
              : item.ofWhat + '_pictures'
            : '',
        name: item.name,
        order_id: item.customerID,
        path: item.path,
        status: 'pending',
        thumbnail,
        updated_at: Date.now(),
        url: '',
        video_of: item.type === 'video' ? item.ofWhat + '_videos' : '',
      })
    }

    // A captured photo used to exist ONLY in memory until its upload finished.
    // The database record was written in exactly two places, both inside
    // uploadMediaItem: the success branch and the error branch. api's
    // updateMediaItem cannot create it either — it checks snapshot.hasChildren()
    // first and no-ops when the record is absent. And nothing persists Redux:
    // configureStore() wraps no persistReducer, so the 'persist/REHYDRATE' cases
    // in this file never fire.
    //
    // So an upload that did not finish — stalled, timed out, app closed — left
    // the photo nowhere at all: not in the database, and gone from memory on the
    // next launch. A rep could document a whole job and lose every photo, with no
    // error and nothing on screen suggesting the work had not been saved.
    //
    // Writing the record here, before the upload is even attempted, makes the
    // photo durable from the moment it is taken. `path` is deliberately blank:
    // it points at a file on one device and means nothing to any other client.
    for (const item of currentBatchToBeProcessed) {
      const isPic = !!item.image_of
      getDatabase()
        .ref(
          isPic
            ? `NewPics/${item.order_id}/${item.name}`
            : `Videos/${item.order_id}/${item.name}`,
        )
        .update({ ...item, path: '', status: 'pending' })
    }

    yield put(
      beganUpload({
        items: currentBatchToBeProcessed.map((it) => ({
          ...it,
          path: IS_WEB ? '' : it.path,
        })),
      }),
    )

    yield all(
      currentBatchToBeProcessed
        .filter((f) => !!f.path)
        .map((mediaItem) => spawn(uploadMediaItem, mediaItem)),
    )
  } catch (e) {
    log(`Error inside mediaUploadWatcher (${e}):`)
  }
}

function* mediaDeletionWatcher({
  payload: { customerID, mediaKind, mediaItemID },
}: ReturnType<typeof requestedMediaDeletion>) {
  if (mediaKind === 'owner_house') {
    api._updateCustomer(customerID, {
      owner_house: '',
    })
  }

  api.deleteMediaItem(customerID, mediaItemID)
}

export function* mediaSaga() {
  yield all([
    takeEvery(requestedMediaUpload, mediaUploadWatcher),
    takeEvery(requestedMediaDeletion, mediaDeletionWatcher),
    call(mediaSubWatcher),
  ])
}

const emptyMediaKindToMedia = () => ({
  adders: {},
  hvac_system: {},
  electrical: {},
  electricity: {},
  id: {},
  insulation: {},
  owner_house: {},
  problem: {},
  roof: {},
  temperature: {},
  thermal: {},
  other_documents: {},
  solar_proposal: {},
  all_exterior_walls_site_survey: {},
  rafters_site_survey: {},
  electrical_panel_site_survey: {},
  all_planes_roof_site_survey: {},
  other_site_survey: {},
  attic_site_survey: {},
  house360_site_survey: {},
  roof_site_survey: {},
  all_appliances_and_labels_site_survey: {},
  eight_foot_msp_site_survey: {},
  existing_solar_system_site_survey: {},
  ground_system_connected_site_survey: {},
  main_breaker_disconnect_closeup_site_survey: {},
  main_panel_breakers_dead_front_included_site_survey: {},
  main_panel_breakers_dead_front_removed_site_survey: {},
  main_panel_label_site_survey: {},
  main_service_panel_site_survey: {},
  mini_split_surroundings: {},
  sub_panels_site_survey: {},
  utility_meter_closeup_site_survey: {},
  windows: {},
  rc_front_house: {},
  rc_address_verification: {},
  rc_3d_view: {},
  rc_solar_panels: {},
  rc_wires_home: {},
  rc_siding: {},
  rc_other_property_wind_damage: {},
  rc_wind_damages: {},
  rc_layers: {},
  rc_roof_inclination: {},
  rc_roof_overview: {},
  rc_gutters: {},
  rc_flashings: {},
  rc_penetrations: {},
  rc_hail: {},
  rc_length_shingle: {},
  rc_drip_edge: {},
  rc_shingles_color: {},
  rc_garage: {},
  rc_ice_shield: {},
  security_cam0: {},
  security_cam1: {},
  security_cam2: {},
  security_cam3: {},
  security_cam4: {},
  security_cam5: {},
  security_cam6: {},
  security_cam7: {},
  security_camera_locations: {},
  security_camera_locations_s4: {},
  security_camera_locations_s3: {},
  security_camera_locations_s2: {},
  security_camera_locations_indoor: {},
  security_doorbell_locations: {},
  security_360_house_view: {},
  security_attic: {},
  security_router_location: {},
  security_post_camera_installation: {},
  security_post_alarm_installation: {},
  security_cctv: {},
  cancellation_letter: {},
  testimonial_video: {},
  signing_recording: {},
})
